<template>
  <div
    v-if="
      currentMission != null &&
      currentMission.typeName === 'ConstellationGenerationRequest'
    "
  >
    <v-col cols="7">
      <span class="ml-4 headline grey--text">{{
        currentMission.name.toUpperCase()
      }}</span>
    </v-col>
    <div class="text-xs-center" v-if="displayComputationWidget">
      <exo-mission-computation></exo-mission-computation>
    </div>
    <v-row align-center justify-center>
      <v-spacer></v-spacer>
      <v-col cols="5" m-2 class="text-right">
        <v-dialog v-if="displayError" v-model="dialog" width="700">
          <template v-slot:activator="{ on: dialog }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on: tooltip }">
                <v-btn
                  large
                  icon
                  class="bug-button"
                  v-on="{ ...dialog, ...tooltip }"
                >
                  <icon class="white--text" scale="1.5" name="user-injured" />
                </v-btn>
              </template>
              <span>Bug report</span>
            </v-tooltip>
          </template>
          <error-log-display
            :root="_self"
            :computationLog="currentResultsLog"
          ></error-log-display>
        </v-dialog>
        <v-tooltip
          bottom
          class="caption justify-center warning--text"
          v-if="currentMission.dirty"
        >
          <template v-slot:activator="{ on }">
            <v-btn
              large
              class="white--text"
              v-on="on"
              @click.stop="requestComputation()"
              color="warning"
            >
              Start Recompute
            </v-btn>
          </template>
          <span
            >You have changed some components of the mission. This results does
            not match the current inputs. Please recompute the mission to
            update.</span
          >
        </v-tooltip>
      </v-col>
    </v-row>
    <div v-if="displayResult && !displayError">
      <v-row justify="space-around">
        <v-col cols="4">
          <exo-form-alert class="mb-2" type="info" v-if="displayNotSSOWarning">
            The best constellation generated has non sun-synchronous planes.
          </exo-form-alert>
          <exo-form-alert
            class="mb-2"
            type="info"
            v-if="displayTooHighNumberOfPlanesWarning"
          >
            The constellation generated could not have less planes than the
            maximum desired.
          </exo-form-alert>
          <v-row>
            <exo-constellation-optimizer-table
              style="width: 100%"
              :items="generationResults"
              title="Generated Constellation"
              :reference="generationResults"
              :optimized="false"
            ></exo-constellation-optimizer-table>
          </v-row>
        </v-col>
        <v-col cols="6">
          <v-card class="cesium-card">
            <v-card-title class="cesium-card-title">
              <h4>Ground Traces</h4>
            </v-card-title>
            <exo-input-date-time
              name="visualizationDate"
              :root="_self"
              :parentObject="
                this.currentResultsLog.results.generatedConstellation[0]
              "
              :forceDisplay="true"
              forcedLabel="Date to visualize the ground tracks"
            >
              <exo-help-dialog margin="mt-3" title="Reference date">
                Allows to take Sun position into account. This is also the
                reference date for the constellation initial RAAN/MLTAN.
              </exo-help-dialog>
            </exo-input-date-time>
            <cesium-viewer
              name="initial"
              :tracesResults="tracesResults"
              :isoStartDate="inputs.visualizationDate"
              :poi="pointOfInterest.isolatedPointsOfInterest"
              :points="meshesPoints"
              :meshes="pointOfInterest.earthMeshes"
            />
          </v-card>
        </v-col>
      </v-row>
      <v-row class="pl-6" justify="space-around">
        <v-col cols="11">
          <v-row>
            <v-text-field
              id="inputName"
              label="Name"
              v-model="name"
              maxlength="100"
              :rules="[inputNameFeedback]"
            ></v-text-field>
            <v-btn
              class="mt-3 ml-3"
              color="primary"
              :disabled="inputNameState"
              v-on:click="saveGeneratedConstellation()"
              >Save</v-btn
            >
          </v-row>
        </v-col>
      </v-row>
      <v-row v-if="this.savedConstellation" no-gutters>
        <v-btn
          class="mt-3 ml-3 white--text"
          color="red"
          :disabled="false"
          v-on:click="requestPerformancesMission()"
          >To Performances</v-btn
        >
        <v-btn
          class="mt-3 ml-3 white--text"
          color="blue"
          :disabled="false"
          v-on:click="requestNumericalSimulation()"
          >To Simulation</v-btn
        >
        <v-btn
          class="mt-3 ml-3 white--text"
          color="green"
          :disabled="false"
          v-on:click="requestOptimisationMission()"
          >To Optimisation</v-btn
        >
      </v-row>
    </div>
  </div>
</template>

<script>
import dashboardComponents from '../../components/Dashboard/index.js'
import constellationComponentsLoader from '../../components/Forms/analyticalConstellationsComponents.js'
import { mapGetters } from 'vuex'
import Vue from 'vue'
import errorMixin from '../../mixins/logHandler.js'
import constellationUtils from '../../mixins/constellationUtils'
import dataTools from '../../tools/data'
import dashboardTools from '../../tools/dashboard.js'
import missionTools from '../../store/mission.js'
import dataDescription from '../../tools/dataDescription.json'
import DashboardGenerationViewer from './DashboardGenerationViewer.vue'
import exoDatetimePicker from '@/components/Forms/dateTimePicker.vue'
import 'cesium/Widgets/widgets.css'
constellationComponentsLoader()

Vue.use(dashboardComponents)
export default {
  name: 'ConstellationGenerationResult',
  mixins: [errorMixin, constellationUtils],
  components: {
    'cesium-viewer': DashboardGenerationViewer,
    'date-time-picker': exoDatetimePicker
  },

  mounted() {
    this.computeEphemerisTraces()
    this.$nextTick(() => {
      this.generationResults = this.getConstellationMarker(
        this.currentResultsLog?.results?.generatedConstellation
      )
    })
    if (this.displayComputationWidget) {
      this.savedConstellation = false
    }
  },

  beforeDestroy() {
    this.currentData = null
    this.emptyResult = false
    this.name = ''
  },

  computed: {
    ...mapGetters({
      currentUser: 'currentUser',
      currentMission: 'currentMission',
      currentResultsLog: 'currentResultsLog'
    }),
    displayComputationWidget() {
      return dashboardTools.displayComputationWidget(this)
    },

    traceBuilderWatcher() {
      // all fields because if one field not related in error, the form need to be updated once result OK
      return [
        this.inputs.visualizationDate,
        this.currentResultsLog?.results?.analyticalConstellation
      ]
    },

    displayResult() {
      return dashboardTools.displayResult(this)
    },
    displayError() {
      return dashboardTools.displayError(this)
    },
    displayNotSSOWarning() {
      return (
        this.currentMission.sunLightUsed &&
        this.currentResultsLog?.results?.generatedConstellation
          ?.computedHomogeneousConstellationGeometries[0]
          .planeDefinitionType === 'ALTITUDE_AND_INCLINATION'
      )
    },
    displayTooHighNumberOfPlanesWarning() {
      return (
        this.currentResultsLog.results.maximumAllowedNumberOfPlanes <
        this.currentResultsLog.results.generatedConstellation
          .computedHomogeneousConstellationGeometries[0].numberOfPlanes
      )
    },
    computationNeeded() {
      return dashboardTools.computationNeeded(this)
    },
    dataLoaded() {
      return dashboardTools.dataLoaded(this)
    },
    dataReady() {
      return this.dataLoaded && this.currentData != null && !this.emptyResult
    },

    loading() {
      return this.currentData == null && this.dataLoaded
    },

    resultLoaded() {
      return (
        this.currentMission &&
        this.currentResultsLog != null &&
        this.currentResultsLog.results != null &&
        this.currentResultsLog.errors != null
      )
    },

    inputNameFeedback() {
      let feedback = true
      if (this.inputNameState !== null && this.inputNameState) {
        feedback =
          "Please only use the allowed characters : a-z A-Z 0-9 ' ',.-+=/_#@$&:%|()]["
      }
      return feedback
    },

    inputNameState() {
      let valid = null
      if (this.name !== '') {
        let allowedChars = RegExp(
          '^[a-zA-Z0-9 ,\\.\\-\\+=\\/_@#\\$&:%\\|\\(\\)\\[\\]]+$'
        )
        valid = allowedChars.test(this.name)
      }
      return !valid
    }
  },

  data() {
    return {
      savedConstellationObject: null,
      generationResults: null,
      tracesResults: {},
      pointOfInterest: {
        isolatedPointsOfInterest: [],
        earthMeshes: []
      },
      errorTrace: '',
      savedConstellation: false,
      name: '',
      dialog: false,
      inputs: {
        visualizationDate: '2022-01-01T12:00:00.000Z'
      },
      meshesPoints: [],
      meshesList: [],
      context: 'AnalyticalConstellationGeneration'
    }
  },

  watch: {
    displayResult() {
      if (
        this.currentResultsLog?.results?.generatedConstellation
          ?.computedHomogeneousConstellationGeometries != null
      ) {
        this.generationResults = this.getConstellationMarker(
          this.currentResultsLog.results.generatedConstellation
        )
      }
    },

    traceBuilderWatcher: function (value) {
      setTimeout(() => {
        this.computeEphemerisTraces()
      }, 200)
    }
  },

  methods: {
    getAPI(objectAPI) {
      return '/users/' + this.currentUser.id + '/' + objectAPI.toLowerCase()
    },

    async computeEphemerisTraces() {
      if (this.resultLoaded && !this.computationNeeded) {
        this.pointOfInterest =
          this.currentResultsLog?.results?.computedEarthZoneOfInterest
        this.updateZOIData()
        if (
          this.currentResultsLog?.results?.generatedConstellation
            ?.computedHomogeneousConstellationGeometries
        ) {
          let generatedConstellation =
            this.currentResultsLog.results.generatedConstellation
          let traces = []
          let payloads = []
          for (let geometry of generatedConstellation.computedHomogeneousConstellationGeometries) {
            if (!geometry.visualizationDate) {
              if (!this.inputs.visualizationDate) {
                geometry.visualizationDate = this.inputs.visualizationDate
              } else {
                geometry.visualizationDate = '2022-01-01T12:00:00.000Z'
              }
            }

            let data = await this.$http
              .post('/physics/computedconstellationorbittraces', geometry)
              .then(function (response) {
                return response.data
              })
              .catch((e, response) => {
                this.errorTrace = this.getErrorMessage(e)
                return null
              })
            if (data == null) {
              traces = null
            } else {
              this.errorTrace = ''
              traces = traces.concat([data.ephemeridesTraces])
              payloads = payloads.concat(data?.payload?.fieldOfView)
            }
          }
          if (traces == null) {
            this.tracesResults = null
          } else {
            let result = {
              traces: traces,
              payloads: payloads
            }
            this.tracesResults = result
          }
        }
      }
    },

    updateZOIData() {
      let meshesPoints = []
      if (
        this.pointOfInterest.earthMeshes &&
        this.pointOfInterest.earthMeshes.length > 0
      ) {
        this.pointOfInterest.earthMeshes.forEach(async (meshFormObject) => {
          this.computeEarthMesh(meshFormObject).then((points) => {
            meshesPoints.push(points)
          })
        })
        this.meshesPoints = meshesPoints
        this.meshesList = this.inputs.earthMeshes
      }
    },

    async computeEarthMesh(meshFormObject) {
      let meshPoints = await this.$http
        .post('/physics/getearthmeshcoordinates', meshFormObject)
        .then(function (response) {
          return response.data
        })
        .catch((e, __) => {
          this.errorTrace = this.getErrorMessage(e)
          return null
        })
      return meshPoints
    },

    getDisplayedValueWithUnit(results, field) {
      let value = results
      if (results == null) {
        return ''
      }
      let type = dataTools.getFieldTypeFromPath(field, this.context, null)
      for (let i = 0; i < field.length; i++) {
        value = value[field[i]]
        if (value == null) {
          return 'undefined'
        }
      }
      if (value == null || isNaN(value) || value === 'NaN') {
        return 'undefined'
      } else {
        value = dataTools.convertSi2Display(type, value)
      }
      return (
        value +
        ' ' +
        dataTools.getUnitDisplayed(
          dataTools.getDefaultGuiUnit(
            dataTools.getFieldTypeFromPath(field, this.context, null)
          )
        )
      )
    },

    buildResultFieldValue(results, field) {
      let type = dataTools.getFieldTypeFromPath(field, this.context, null)
      if (
        dataDescription[type] != null &&
        (dataDescription[type].type === 'int' ||
          dataDescription[type].type === 'double' ||
          dataDescription[type].type === 'integer')
      ) {
        return this.getDisplayedValueWithUnit(results, field)
      } else if (field[0] === 'initialRaans') {
        return this.getDisplayedAngleList(results, field)
      } else {
        return this.getDisplayedString(results, field)
      }
    },

    getDisplayedString(results, field) {
      let value = results
      if (value == null) {
        return ''
      }
      for (let i = 0; i < field.length; i++) {
        if (value == null) {
          return ''
        }
        value = value[field[i]]
      }
      if (value == null) {
        return ''
      }
      return (
        value +
        ' ' +
        dataTools.getUnitDisplayed(
          dataTools.getDefaultGuiUnit(
            dataTools.getFieldTypeFromPath(field, this.context, null)
          )
        )
      )
    },

    getDisplayedAngleList(results, field) {
      let values = results[field[0]]
      let stringToDisplay = ''
      if (!Array.isArray(values) || !values.length) {
        return ''
      }
      for (let value of values) {
        /// TODO convertrad/deg dataTools.deg2rad(
        let valueInDegres = dataTools.displayNdigits(
          dataTools.rad2deg(value),
          2
        )
        stringToDisplay += valueInDegres + '; '
      }
      stringToDisplay =
        stringToDisplay.substring(0, stringToDisplay.length - 2) + ' (deg)'
      return stringToDisplay
    },

    getConstellationMarker(constellation) {
      return [
        {
          name: 'Number of planes',
          value: this.displayCustomValue(constellation, 'numberOfPlanes')
        },
        {
          name: 'Number of spacecrafts',
          value: this.displayCustomValue(constellation, 'numberOfSpacecrafts')
        },
        {
          name: 'Altitude (km)',
          value: this.displayCustomValue(constellation, 'altitudeRange')
        },
        {
          name: 'Inclination range (deg)',
          value: this.displayCustomValue(constellation, 'inclinationRange')
        }
      ]
    },

    displayCustomValue(data, field) {
      switch (field) {
        case 'numberOfPlanes':
          return this.computeNumberOfPlanes(data)
        case 'numberOfSpacecrafts':
          return this.computeNumberOfSpacecrafts(data)
        case 'altitudeRange':
          return this.computeAltitudeRange(data)
        case 'inclinationRange':
          return this.computeInclinationRange(data)
      }
      return ''
    },

    computeNumberOfPlanes(data) {
      let geometries = data.computedHomogeneousConstellationGeometries
      let numberOfPlanes = 0
      for (let geometry of geometries) {
        numberOfPlanes = numberOfPlanes + geometry.numberOfPlanes
      }
      return numberOfPlanes
    },

    computeNumberOfSpacecrafts(data) {
      let geometries = data.computedHomogeneousConstellationGeometries
      let numberOfSpacecrafts = 0
      for (let geometry of geometries) {
        numberOfSpacecrafts =
          numberOfSpacecrafts +
          geometry.numberOfPlanes * geometry.numberOfSpacecraftsPerPlane
      }
      return numberOfSpacecrafts
    },

    computeAltitudeRange(data) {
      return (
        Math.round(
          data.computedHomogeneousConstellationGeometries[0].plane.altitude
        ) / 1e3
      )
    },

    computeInclinationRange(data) {
      let geometries = data.computedHomogeneousConstellationGeometries
      let minInclination = 181
      let maxInclination = 0
      for (let geometry of geometries) {
        let currentInclination = dataTools.rad2deg(geometry.plane.inclination)
        if (currentInclination < minInclination) {
          minInclination = Math.round(currentInclination * 1e2) / 1e2
        }
        if (currentInclination > maxInclination) {
          maxInclination = Math.round(currentInclination * 1e2) / 1e2
        }
      }
      if (minInclination === maxInclination) {
        return minInclination
      } else {
        return [minInclination, maxInclination]
      }
    },

    async saveGeneratedConstellation() {
      let computedConstellation =
        this.currentResultsLog.results.generatedConstellation
      computedConstellation.name = this.name
      let i = 1
      for (let geometry of computedConstellation.computedHomogeneousConstellationGeometries) {
        geometry.name = this.name + '(' + i + ')'
        i = i + 1
      }
      try {
        this.savedConstellationObject = (
          await this.$http.post(
            this.getAPI(`constellationdefinitions/fromresult`),
            computedConstellation
          )
        ).data
        this.displayMessage(
          'Constellation saved as :' + this.name,
          '',
          'notification',
          'success',
          1500
        )
        this.savedConstellation = true
      } catch (e) {
        this.displayMessage(
          'Constellation save failed.',
          e,
          'notification',
          'error'
        )
      }
    },

    redirectToMissionSetting(typeName) {
      this.$router.push({
        path: '/' + missionTools.missionUrlMapping[typeName].settings
      })
    },

    requestPerformancesMission() {
      const mission = {
        constellation: this.savedConstellationObject,
        typeName: 'ConstellationPerformanceAnalysisRequest'
      }
      this.$router.push({ path: '/analyticalconstellationsimulation' })
      this.sendMissionEvent('create-mission', mission)
    },

    requestNumericalSimulation() {
      const mission = {
        constellation: this.savedConstellationObject,
        typeName: 'ConstellationSimulationRequest'
      }
      this.$router.push({ path: '/numericalconstellationmission' })
      this.sendMissionEvent('create-mission', mission)
    },

    requestOptimisationMission() {
      const mission = {
        initialConstellation: this.savedConstellationObject,
        typeName: 'ConstellationOptimizationRequest',
        zoneOfInterest: this.currentMission.earthZoneOfInterest
      }
      this.$router.push({ path: '/constellationrevisitoptimizationmission' })
      this.sendMissionEvent('create-mission', mission)
    },

    sendMissionEvent(eventName, mission) {
      // event send to root because it should be treated by sub components
      // use nextTick to be sure mission components are created and can handle events
      Vue.nextTick(() => {
        this.$root.$emit(eventName, mission)
      })
    },

    requestComputation() {
      this.$router.push({ path: '/constellationgenerationmission' })
      this.savedConstellation = false
      Vue.nextTick(() => {
        // use nextTick to be sure mission components are created and can handle events
        this.$root.$emit('request-computation-constellation-generation')
      })
    }
  }
}
</script>

<style scoped>
.cesium-card {
  display: grid;
  grid-auto-flow: column;
  grid-template-rows: 10% 10% 80%;
  height: 60vh;
  transition: all 0.3s linear;
}

.cesium-card-title {
  display: inline-flex;
  align-items: center;
}

.cesium-card-title .v-input,
.cesium-card-title .v-input__slot {
  margin: 0;
}

.cesium-card-title .v-messages {
  display: none !important;
}

.bug-button {
  background: #ff5252 !important;
}
</style>
